import * as Helpers from "./helpers";
import Typography from "@portal/components/atoms/v2/typography";
import usePortfolios from "@portal/hooks/usePortfolios";
import useBankAccounts from "@portal/hooks/useBankAccounts";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import { useState, useMemo } from "react";
import { useRobot } from "@portal/app/context/robotProvider";
import { MainBox } from "../components/mainBox";
import { OnAdvisorChange } from "../components/handlers";
import { PeriodDayOptions, AccountOptions, AdvisorOptions, NameInputOptions } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { CreateAskDialog } from "../../ask/create/createAskDialog";
import UseCreateSuitabilityReport from "@portal/hooks/useCreateSuitabilityReport";
import YearlyIncrease, { getYearlyIncreaseIndex } from "../../agreements/shared/yearlyIncrease";
import Input from "@portal/components/atoms/v2/input";
import { useCreateSavingsAgreement } from "@portal/app/context/createSavingsAgreementProvider";
import { RobotFooterComponent } from "../components/footer";

export const CompleteSavingsPage = ({ isDefaultLanguage, pageIndex, centerId }: IPageProps) => {
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const [isLoading, setIsLoading] = useState(false);
  const {
    request,
    setSuitabilityReport,
    setYearlyIncreasePercent,
    setSavingsAgreementName,
    setSavingsAgreementPeriodDayNumber,
    setSavingsAgreementAdvisorCarriedOut,
    setSavingsAgreementAdvisorName,
    IsValidRequest,
  } = useCreateSavingsAgreement();
  const [showCreateAskModal, setShowCreateAskModal] = useState(false);
  const [agreementName, setAgreementName] = useState(request.agreementData.adjustmentComment ?? "");
  const [selectedPeriodDay, setSelectedPeriodDay] = useState<number>(
    Helpers.MapPeriodDayToIndex(request.agreementData.periodDayNumber ?? 0),
  );
  const [selectedItem, setSelectedItem] = useState(getYearlyIncreaseIndex(request.agreementData.yearlyIncreasePercent ?? 0));

  const { data: portfolios } = usePortfolios();
  const { riskLevelRequest, riskLevelResponse, summedFee, savingProposalRequest } = useRobot();
  const isAskEnabled = Helpers.IsAskEnabled(riskLevelResponse.data);

  const { bankAccounts } = useBankAccounts();
  const filteredPortfolios = useMemo(
    () => (portfolios ? Helpers.FilterPortfoliosByCenterId(portfolios, centerId) : []),
    [centerId, portfolios],
  );
  const selectedPortfolio = useMemo(
    () => filteredPortfolios.find(pf => pf.portfolioId === request.portfolioId && (pf.taxationType !== "ASK" || isAskEnabled)),
    [filteredPortfolios, isAskEnabled, request.portfolioId],
  );

  const { data: customerInfo } = useCustomerInfo();
  const { submitSuitabilityReport } = UseCreateSuitabilityReport();
  const suitabilityReport = Helpers.GetSuitabilityReport(
    centerId,
    request,
    isDefaultLanguage,
    savingProposalRequest.savingAgreementData,
    riskLevelRequest,
    customerInfo,
    summedFee,
  );

  const handleYearlyIncreaseChange = (i: number, percent: number) => {
    setYearlyIncreasePercent(percent);
    setSelectedItem(i);
  };

  const createSuitabilityReport = async () => {
    setIsLoading(true);
    const response = await submitSuitabilityReport(suitabilityReport);
    if (response?.text) setSuitabilityReport(response?.text);
    setIsLoading(false);
  };

  return (
    <MainBox>
      <TextLine />
      <NameInputOptions
        name={agreementName ?? ""}
        header={texts.PAGE_16_NAME}
        setAgreementName={setAgreementName}
        setSavingsAgreementName={setSavingsAgreementName}
      />
      <PeriodDayOptions
        isDefaultLanguage={isDefaultLanguage}
        selectedPeriodDayNumber={selectedPeriodDay}
        header={texts.PAGE_16_DATE}
        setSelectedPeriodDayNumber={setSelectedPeriodDay}
        setSavingsAgreementPeriodDayNumber={setSavingsAgreementPeriodDayNumber}
      />
      {!isAskEnabled && <InfoBoxComponent texts={[texts.PAGE_16_INFO_BOX]} />}
      <AccountOptions
        selectedPortfolio={selectedPortfolio}
        bankAccounts={bankAccounts}
        selectedBankAccountNumber={request.agreementData.cashAccount}
        otherBankAccountNumber={request.agreementData.otherCashAccount}
        isDefaultLanguage={isDefaultLanguage}
        isAskDisabled={!isAskEnabled}
        createAskAccount={isAskEnabled}
        setShowCreateAskModal={setShowCreateAskModal}
        showOtherBankAccount={
          savingProposalRequest.savingAgreementData.monthlyAmount > 0 &&
          savingProposalRequest.savingAgreementData.oneTimeDeposit > 0
        }
      />
      <div className="my-5">
        <AdvisorOptions
          isDefaultLanguage={isDefaultLanguage}
          advisorCarriedOut={request.agreementData.advisorCarriedOut}
          setSavingsAgreementAdvisorCarriedOut={setSavingsAgreementAdvisorCarriedOut}
        />
        {request.agreementData.advisorCarriedOut && (
          <div className="flex flex-col mt-2">
            <Typography variant="Body">{texts.PAGE_16_ADVISOR}</Typography>
            <div className="border border-blueLight rounded-md">
              <Input
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  OnAdvisorChange(e.target.value, setSavingsAgreementAdvisorName)
                }
                value={request.agreementData.advisorName}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mb-2">
        <YearlyIncrease handleChange={handleYearlyIncreaseChange} selectedItem={selectedItem} />
      </div>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={!IsValidRequest()}
        onNextClick={() => createSuitabilityReport()}
        loading={isLoading}
      />
      {showCreateAskModal && (
        <CreateAskDialog centerId={centerId} isDefaultLanguage={isDefaultLanguage} setShowAskComponent={setShowCreateAskModal} />
      )}
    </MainBox>
  );
};
